import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/cn';

export type PaginatedListProps<T> = {
  items: readonly T[];
  pageSize?: number;
  renderItem: (item: T, index: number) => ReactNode;
  keyExtractor: (item: T, index: number) => string;
  emptyMessage?: ReactNode;
  className?: string;
  listClassName?: string;
  /** Reset to page 0 when this changes (e.g. filter key). */
  resetKey?: string | number;
};

/** Viewport-safe list: paginate instead of scrolling the main shell. */
export function PaginatedList<T>({
  items,
  pageSize = 5,
  renderItem,
  keyExtractor,
  emptyMessage,
  className,
  listClassName,
  resetKey,
}: PaginatedListProps<T>): ReactElement {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount - 1);

  useEffect(() => {
    setPage(0);
  }, [items.length, resetKey]);

  useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const start = safePage * pageSize;
  const slice = items.slice(start, start + pageSize);

  if (items.length === 0) {
    return (
      <div className={cn('min-h-0 flex-1 overflow-hidden', className)}>
        {emptyMessage ?? (
          <p className="text-dg-secondary text-dg-fg-muted dark:text-slate-400">
            {t('common.empty')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-hidden', className)}>
      <ul className={cn('min-h-0 flex-1 list-none space-y-2 overflow-hidden', listClassName)}>
        {slice.map((item, i) => (
          <li key={keyExtractor(item, start + i)}>{renderItem(item, start + i)}</li>
        ))}
      </ul>
      {items.length > pageSize ? (
        <nav
          className="flex shrink-0 items-center justify-between gap-2 border-t border-dg-border-subtle pt-2 dark:border-slate-800"
          aria-label={t('pagination.navAria')}
        >
          <button
            type="button"
            disabled={safePage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-dg-control border border-dg-border bg-dg-bg-surface disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900"
            aria-label={t('pagination.prev')}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <span className="text-dg-label font-medium text-dg-fg-muted tabular-nums dark:text-slate-400">
            {t('pagination.pageOf', { current: safePage + 1, total: pageCount })}
          </span>
          <button
            type="button"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-dg-control border border-dg-border bg-dg-bg-surface disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900"
            aria-label={t('pagination.next')}
          >
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
        </nav>
      ) : null}
    </div>
  );
}
